import { Fragment, useEffect, useRef, useState } from "react";
import { GlassButton } from "./GlassButtons";
import { Localization } from "@viamap/viamap2-common";

import { BsPalette2 } from "react-icons/bs";
import { autoUpdate, FloatingPortal, offset, useDismiss, useFloating, useHover, useInteractions } from "@floating-ui/react";
import { ColorPicker } from "./ComponentUtils";
import { Reorder } from "framer-motion";
import { Typeahead } from "react-bootstrap-typeahead";
import { ArrangeVertical } from "iconsax-react";




export function GlassInputGroupTitle(props: { children }) {
   return (
      <div className="GlassInputGroupTitle">{props.children}</div>
   )
}

export function GlassInputGroupEscape(props: { children }) {
   return (
      <div className="GlassInputGroupEscape">{props.children}</div>
   )
}

export function GlassInputGroup(props: { children, style?, autoFocus?: boolean | number, onSubmit?: (e: any) => void, onEscape?: (e: any) => void }) {
   const ref = useRef<HTMLFormElement>(null)

   useEffect(() => {
      if (typeof props.autoFocus === "number") {
         ref.current?.parentElement?.querySelectorAll(':is(input, select, checkbox)').forEach((a, idx) => {
            if (idx === props.autoFocus && a instanceof HTMLElement) {
               a.focus()
            }
         })
      } else if (props.autoFocus) {
         ref.current?.parentElement?.querySelectorAll(':is(input, select, checkbox)').forEach((a, idx) => {
            if (idx === props.autoFocus && a instanceof HTMLElement) {
               a.focus()
            }
         })
      }
   }, [])

   return (
      <form className="GlassInputGroup" ref={ref} style={props.style}
         onSubmit={(e) => { e.preventDefault(); props.onSubmit?.(e) }}
         onKeyDown={(e) => {
            if (e.key === "Escape") { props.onEscape?.(e) }
            if (e.key === "Enter") {
               if (document.activeElement instanceof HTMLTextAreaElement) return


               e.preventDefault();
               let focusInput = false
               let shouldSubmit = true
               ref.current?.parentElement?.querySelectorAll(':is(input, select, checkbox)').forEach((a) => {
                  if (a instanceof HTMLElement && focusInput && shouldSubmit) {
                     a.focus()
                     shouldSubmit = false
                  }
                  if (a === document.activeElement) {
                     focusInput = true
                  }
               })
               if (shouldSubmit) {
                  props.onSubmit?.(e)
               }
            }

         }}
      >
         {props.children}
      </form>
   )
}

type ColorPalleteProps = {
   colorSet: string[],
   defaultColor: string,
   getter: () => string,
   onChange: (color: string) => void
}
export function GlassColorPalette({
   colorSet,
   defaultColor,
   getter,
   onChange
}: ColorPalleteProps) {
   const [showColorPicker, setShowColorPicker] = useState<boolean>(false)
   const { refs, floatingStyles } = useFloating({
      whileElementsMounted: autoUpdate,
      placement: "left",
   });


   return (
      <>
         <div className="GlassColorPalette">
            {colorSet.includes(getter()) ? colorSet.map((color, idx) =>
               idx === 0 && colorSet.includes(color, 1) ? null :
                  <Fragment key={color}><GlassColorBox selected={color === getter()} onClick={() => onChange(color)} color={color} /></Fragment>
            )
               :
               <>
                  <GlassColorBox selected={true} color={getter()} />
                  <GlassButton
                     onClick={() => { onChange(defaultColor); }}
                  >
                     {Localization.getText("Restore default")}
                  </GlassButton>
               </>
            }
            <div className="Cog" style={{ color: getter() }} ref={refs.setReference}>
               <BsPalette2 style={{ width: "16px", height: "16px", marginLeft: "auto", cursor: "pointer" }} onClick={() => setShowColorPicker(true)} title={Localization.getText("Click to edit color")} />
            </div>
         </div>
         {showColorPicker ? (
            <FloatingPortal >
               <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: "1001" }} >
                  <ColorPicker
                     color={getter() || "blue"}
                     onCommit={(color) => {
                        onChange(color)
                        setShowColorPicker(false)
                     }}
                     onCancel={() => setShowColorPicker(false)}
                  />
               </div>
            </FloatingPortal>
         ) : null}
      </>
   )
}

export function GlassColorBox(props: { color: string, selected?: boolean, onClick?: (e: any) => void }) {
   return (
      <div onClick={props.onClick} className={"GlassColorBox" + (props.selected ? " selected" : "")} style={({ "--color": props.color } as any)} ></div>
   )
}

export function GlassTextInput(
) {

   return (
      <>
         <div className="GlassInputLabel"></div>
         <div className="GlassInputField"></div>
      </>
   )
}

export function GlassTextInputTransformer(props: { label, children, top?: string }) {

   return (
      <>
         <div className={"GlassInputLabel " + (props.top ? "top" : "")} style={{
            marginTop: props.top || "0px"
         }} >{props.label}</div>
         <div className="GlassInputField">{props.children}</div>
      </>
   )
}

export function GlassInputControl(props: { children }) {
   return (
      <div className="GlassInputControl">{props.children}</div>
   )
}

export function GlassInputControlLabel(props: { children, top?: boolean }) {
   return (
      <div className={"GlassInputLabel" + (props.top ? " top" : "")}>{props.children}</div>
   )
}
/**
 * Glass Input Control Button with tooltip GICButton
 */
export function GICButton({ children, title, ...props }: { children, title?: string } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
   const [tooltipOpen, setTooltipOpen] = useState(false);
   const { refs, floatingStyles, context: tooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      placement: props["title-placement"],
      open: tooltipOpen,
      middleware: [offset(5)],
      onOpenChange: setTooltipOpen,
   });

   const {
      getFloatingProps: getTooltipFloatingProps
   } = useInteractions([useHover(tooltipContext), useDismiss(tooltipContext)]);


   return (
      <>
         <button {...props} ref={refs.setReference} type="button" >
            {children}
         </button>
         {Boolean(title && tooltipOpen) &&
            <FloatingPortal>
               <div ref={refs.setFloating} className="GlassButtonToolTip" style={floatingStyles} {...getTooltipFloatingProps()} >
                  {title || "ThisIsToolTIp"}
               </div>
            </FloatingPortal>
         }
      </>
   )
}



export function GlassTypeAheadReordering(props: {
   layerId: number,
   id?: string,
   options?: { label, value }[],
   selected?: { label, value }[],
   placeholder?: string,
   emptyLabel?: string,
   onChange?: (a: any[]) => void,
   settingComponent?: React.ReactNode,
}) {

   const [mode, setMode] = useState<"default" | "reorder">("default")

   return (
      <div style={{ width: "100%" }}>
         <div style={{ display: "flex" }}>
            {/* Typeahead */}
            <div style={{ flex: 1 }}>
               <Typeahead
                  multiple={true}
                  id={props.id}
                  emptyLabel={props.emptyLabel}
                  placeholder={props.placeholder}
                  className={"GlassReorderTypeahead " + (mode === "reorder" ? " reorder" : "default")}
                  options={props.options || []}
                  selected={props.selected || []}
                  onChange={(a) => props.onChange?.(a)}
                  renderToken={(option, rTprops, index) => {
                     if ((index !== 0 && mode == "reorder")) return <></>
                     if ((mode == "default")) {
                        return (
                           <Fragment key={index}>
                              <div className={"rbt-token rbt-token-removeable"} >
                                 <div className="rbt-token-label" >{option["label"]}</div><button className="close btn-close rbt-close rbt-token-remove-button" onClick={() => rTprops.onRemove?.(option)}>×</button>
                              </div>
                           </Fragment>
                        )
                     }
                     if (index !== 0) { return <></> }
                     return (
                        <Reorder.Group values={props.selected?.map(a => a.value) || []} className="rbt-input-wrapper" as="div" axis="y" animated={false} onReorder={(a) => props.onChange?.(a.map((a) => props.options?.find((b) => b.value === a) || { label: a, value: a }))} >
                           {(props.selected || []).map((option) => {
                              return (<Reorder.Item as="div" dragListener={mode == "reorder"} className={"rbt-token rbt-token-removeable reOrder"} value={option.value} key={option.label} >
                                 <div className="rbt-token-label" onMouseDown={(e) => e.preventDefault()} >{option.label}</div><button className="close btn-close rbt-close rbt-token-remove-button" onClick={() => rTprops.onRemove?.(option)}>×</button>
                              </Reorder.Item>
                              )
                           })}
                        </Reorder.Group>
                     )
                  }}
               />
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>


               <div
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                     e.preventDefault();
                     e.stopPropagation();
                     setMode(mode === "reorder" ? "default" : "reorder");
                     return false
                  }}
                  className="changeMode"
                  style={{ cursor: "pointer", padding: "1px 4px" }}
               >
                  <ArrangeVertical size="14" color="var(--viamapGreen)" />
               </div>

               {props.settingComponent}
            </div>
         </div>
      </div>
   )
}